import { Field, Form, Formik } from 'formik';
import memoize from 'lodash.memoize';
import PropTypes from 'prop-types';
import React, { createRef } from 'react';

import RichTextConverter from '../RichText/RichTextConverter';
/*import { smoothScrollToOffset } from '../../../helpers/utilities';*/
//import { ReactComponent as AjaxLoader } from '../../../images/ajax-loader.svg';
import DynamicFormFields from './DynamicFormFields/DynamicFormFields';
import ErrorFocus from './DynamicFormFields/ErrorFocus';
import * as styles from './FormWrapper.module.css';
import { submitGenericForm } from './submitForm';

/**
 * Smoothly scrolls to a specified element with optional offset for padding/margins
 *
 * @param element The element to scroll to.  Either the value of `document.querySelector()`
 * or React `ref.current`
 * @param offsetAdjust Pixels as number to scroll above element
 */
export const smoothScrollToOffset = (element, offsetAdjust) => {
  if (!element || typeof window === 'undefined') {
    return false;
  }
  const rect = element.getBoundingClientRect();
  const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
  const offset = rect.top + scrollTop;
  window.scrollTo({
    left: 0,
    top: offset - (offsetAdjust || 40),
    behavior: 'smooth',
  });
};

const MapMarketoFields = (fields) => {
  const nonFields = ['htmltext'];
  const transformType = (type) => {
    switch (type.toLowerCase()) {
      case 'text':
      case 'email':
      case 'telephone':
      case 'url':
      case 'number':
      case 'hidden':
        return 'input';
      case 'textArea':
        return 'textarea';
      default:
        return type;
    }
  };

  if (!fields) {
    return null;
  }

  return fields
    .filter((f) => {
      return nonFields.indexOf(f.dataType) === -1;
    })
    .map((f) => {
      const split = f.id.split('___');
      const name = split[split.length - 1];

      return Object.assign(
        {},
        {
          type: transformType(f.dataType),
          name: name,
          label: f.label || '',
          order: f.rowNumber,
          ...f,
        }
      );
    });
};

class FormWrapper extends React.Component {
  constructor(props) {
    super(props);
    this.initialValues = {};
    this._isMounted = false;
    this.fields = [];
    this.state = {
      submitting: false,
      success: false,
      error: false,
    };

    this.errorRef = createRef();
    this.submit = this.submit.bind(this);
  }

  componentDidMount() {
    this._isMounted = true;
    if (!this.props.fields) return;

    this.props.fields.forEach((field) => {
      if (!field.name) return;
      if (field.dataType === 'checkboxes') {
        this.initialValues[`${field.name}`] = [];
      } else {
        this.initialValues[field.name] = '';
      }
    });
    const fields = MapMarketoFields(this.props.fields);
    this.setState({ fields });
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  async submit(values) {
    //TODO: Review language
    const errorMessage = 'Sorry, something went wrong on our end.  Please try again.';
    this.setState({ submitting: true });
    if (values['middlename']) {
      await this.setState({
        submitting: false,
        error: errorMessage,
      });
      return;
    }

    const { success } = await submitGenericForm(
      this.state.fields,
      values,
      this.props.programName || this.props.form.marketoProgramName
    );

    if (success) {
      this.setState({ submitting: false, success: true });
      if (window && window.FS) {
        window.FS.event('Marketo Form', {
          type: 'Form was successful',
          formID: this.props.form?.marketoId?.toString(),
        });
      }
      window['optimizely'] = window['optimizely'] || [];
      window['optimizely'].push({
        type: 'event',
        eventName: 'successful_demo_submission',
        tags: {},
      });
      if (!!window._6si) {
        try {
          window._6si.send(values);
        } catch (e) {
          // console.error(e);
        }
      }
      if (this.props.onSuccess) {
        await this.props.onSuccess();
      }
    } else {
      this.props.onFail && this.props.onFail();
      if (window && window.FS) {
        window.FS.event('Marketo Form', {
          type: 'Form Submit Failure',
          values,
          formName: this.props.formName,
          formID: this.props.form?.marketoId?.toString(),
        });
      }
      await this.setState({
        submitting: false,
        error: errorMessage,
      });
      smoothScrollToOffset(this.errorRef.current);
    }
  }

  render() {
    const { props } = this;
    return (
      <>
        <Formik onSubmit={this.submit} name={props.form.name} initialValues={this.initialValues}>
          {({ values, setFieldValue, touched, errors }) => {
            const { success, submitting } = this.state;
            const hasErrors = Object.keys(errors).length > 0;
            const displayMidSuccess = props.form.name === 'Where Did You Hear About Us';
            const { successMessage } = this.props;
            const defaultMessage = <span>Thank you! A team member will follow up within one business day.</span>;
            return (
              <div className={[this.props.className, 'api-form'].join(' ')}>
                <div
                  className={[styles.overlay, props.overlayOverride, submitting ? styles.showOverlay : ''].join(' ')}
                >
                  <div className={styles.loaderWrap}>
                    <div className={styles.spinner} />
                  </div>
                </div>
                {!success && (
                  <Form className={styles.formContainer} noValidate={true} netlify-honeypot={'middlename'}>
                    {/*TODO: This is kind of hacky - but for now it will have to do*/}
                    {displayMidSuccess && (
                      <div className={styles.midSuccess}>
                        <span>Thank you! A team member will follow up within one business day.</span>
                      </div>
                    )}
                    <div className={[styles.formWrap, displayMidSuccess ? styles.span1 : ''].join(' ')}>
                      {this.state.error && (
                        <span ref={this.errorRef} className={styles.error}>
                          {this.state.error}
                        </span>
                      )}
                      <DynamicFormFields
                        fields={this.state.fields}
                        values={values}
                        setFieldValue={setFieldValue}
                        submitting={submitting}
                        formName={props.form.name}
                        errors={errors}
                        touched={touched}
                        marketoValidation={props.marketoValidation}
                      />

                      <ErrorFocus />
                      <Field
                        component="input"
                        type="text"
                        className={styles.middle}
                        autoComplete="off"
                        name="middlename"
                      />
                    </div>
                    <button className={styles.button} type="submit" disabled={hasErrors || submitting}>
                      {props.form.buttonLabel?.toUpperCase() || 'Submit'}
                    </button>

                    {/*<SubmitButton styleType={props.buttonType} disabled={submitting} label={props.submitText} />*/}
                  </Form>
                )}
                {/*Display a success message if provided*/}
                {success && (
                  <>
                    {successMessage && successMessage.raw && <RichTextConverter copy={successMessage} />}
                    {successMessage && <span>{successMessage}</span>}
                    {!successMessage && defaultMessage}
                  </>
                )}
              </div>
            );
          }}
        </Formik>
      </>
    );
  }
}

FormWrapper.defaultProps = {
  fields: [],
  overlayOverride: '',
};

FormWrapper.propTypes = {
  /** Submit button text if not providing a button */
  submitText: PropTypes.string,
  /** Array of form fields as structured by Marketo's api */
  fields: PropTypes.array.isRequired,
  /** Optionally set initial form values */
  initialValues: PropTypes.object,
  /** General error message when something goes wrong */
  errorMessage: PropTypes.string,
  /** Formik callback for submission */
  onSubmit: PropTypes.func.isRequired,
  /** Callback from using component */
  onSuccess: PropTypes.func,
  /** Callback on failure */
  onFail: PropTypes.func,
  /** The name of the form */
  formName: PropTypes.string.isRequired,
  /** What to display on submission success */
  children: PropTypes.any,
  /** Optional classname for the wrapping component */
  className: PropTypes.string,
  /** CSS class overrides for the submission overlay */
  overlayOverride: PropTypes.string,
  /** Show legacy marketo validation errors */
  marketoValidation: PropTypes.bool,
};

export default FormWrapper;
